import React, { useState, useEffect } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { getAuth, signInAnonymously } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/Game.css";
import firebaseConfig from "../firebaseConfig";
import logo from "../images/logo.png";
import googlePlayBadge from "../images/googlePlayBadge.png";

// ---------- Simple "Encryption" Functions ----------
const M = 100000007; // A prime number greater than any date number (YYYYMMDD)
const A = 7919; // Multiplier (should be coprime with M)
const B = 12345; // Adder

function modInverse(a, m) {
  let m0 = m,
    x0 = 0,
    x1 = 1;
  if (m === 1) return 0;
  while (a > 1) {
    let q = Math.floor(a / m);
    let t = m;
    m = a % m;
    a = t;
    t = x0;
    x0 = x1 - q * x0;
    x1 = t;
  }
  if (x1 < 0) x1 += m0;
  return x1;
}

function gtag_report_conversion(url) {
  var callback = function () {
    if (typeof url != "undefined") {
      window.location = url;
    }
  };
  gtag("event", "conversion", {
    send_to: "AW-949766537/JgnBCIjou4YYEImT8cQD",
    event_callback: callback,
  });
  return false;
}

function encryptDate(dateStr) {
  const numStr = dateStr.replace(/-/g, "");
  const x = parseInt(numStr, 10);
  const y = (x * A + B) % M;
  let hex = y.toString(16).padStart(8, "0");
  return hex.split("").reverse().join("");
}

function decryptDate(encStr) {
  const rev = encStr.split("").reverse().join("");
  const y = parseInt(rev, 16);
  const A_inv = modInverse(A, M);
  const x = (((y - B + M) % M) * A_inv) % M;
  let xStr = x.toString().padStart(8, "0");
  return `${xStr.substring(0, 4)}-${xStr.substring(4, 6)}-${xStr.substring(
    6,
    8
  )}`;
}

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const shuffleArray = (array) => array.sort(() => Math.random() - 0.5);
const categoryColors = ["#FFDDC1", "#C1E1FF", "#D4F4DD", "#FFD1DC"];

const CorrectGroups = ({ correctGroups }) => {
  const groups = Array.isArray(correctGroups)
    ? correctGroups
    : Object.values(correctGroups);
  return (
    <div
      className="correct-groups"
      style={{ width: "100%", maxWidth: "640px" }}
    >
      {groups.map((group, index) => (
        <motion.div
          key={index}
          className="correct-group"
          style={{
            backgroundColor: categoryColors[index % categoryColors.length],
          }}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <span className="group-category">{group[0].category}</span>
          <div className="group-words">
            {group.map((word) => (
              <span key={word.word} className="group-word">
                {word.word}
              </span>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

const WordGrid = ({ words, selectedWords, handleWordClick, error }) => (
  <div className="word-grid">
    {words.map((w) => (
      <motion.div
        key={w.word}
        className={`word-tile ${
          selectedWords.includes(w.word) ? "selected" : ""
        } ${error && selectedWords.includes(w.word) ? "incorrect" : ""}`}
        whileTap={{ scale: 0.85 }}
        onClick={() => handleWordClick(w.word)}
      >
        {w.word}
      </motion.div>
    ))}
  </div>
);

const ConnectionsGame = () => {
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  const queryParams = new URLSearchParams(window.location.search);
  const encryptedParam = queryParams.get("id");
  const gameDate =
    encryptedParam && decryptDate(encryptedParam)
      ? decryptDate(encryptedParam)
      : queryParams.get("date") || today;

  const [userId, setUserId] = useState(null);
  const [words, setWords] = useState([]);
  const [allWords, setAllWords] = useState([]);
  const [selectedWords, setSelectedWords] = useState([]);
  const [correctGroups, setCorrectGroups] = useState({});
  const [error, setError] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [triesLeft, setTriesLeft] = useState(5);
  const [showSolution, setShowSolution] = useState(false);
  const [riddleIndex, setRiddleIndex] = useState(null);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [gameStateLoaded, setGameStateLoaded] = useState(false);
  const [showThreePopup, setShowThreePopup] = useState(false);
  const [showPreviousGames, setShowPreviousGames] = useState(false);
  const [previousGames, setPreviousGames] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  // New state variable to track whether a daily riddle exists
  const [dailyRiddleExists, setDailyRiddleExists] = useState(true);

  // Anonymous Firebase login
  useEffect(() => {
    const auth = getAuth();
    signInAnonymously(auth)
      .then((userCredential) => {
        setUserId(userCredential.user.uid);
        console.log("Signed in anonymously with uid", userCredential.user.uid);
      })
      .catch((error) => console.error("Anonymous sign in error", error));
  }, []);

  // Fetch today's (or specified date's) words from Firestore
  useEffect(() => {
    const fetchWords = async () => {
      const wordsCollection = collection(db, "connections_words");
      const wordsSnapshot = await getDocs(wordsCollection);
      const docData = wordsSnapshot.docs
        .find((doc) => doc.id === gameDate)
        ?.data();
      if (docData) {
        const shuffledWords = shuffleArray(docData.words);
        setWords(shuffledWords);
        setAllWords(shuffledWords);
        setRiddleIndex(docData.index || "?");
      } else {
        console.error("No words found for", gameDate);
        setDailyRiddleExists(false);
      }
    };
    fetchWords();
  }, [gameDate]);

  // Load the user's game state
  useEffect(() => {
    const loadGameState = async () => {
      if (userId) {
        const stateRef = doc(db, "userGameStates", `${userId}-${gameDate}`);
        const stateSnap = await getDoc(stateRef);
        if (stateSnap.exists()) {
          const stateData = stateSnap.data();
          if (stateData.triesLeft !== undefined)
            setTriesLeft(stateData.triesLeft);
          if (stateData.correctGroups)
            setCorrectGroups(stateData.correctGroups);
          if (stateData.completed) {
            setGameCompleted(true);
            setShowSolution(true);
          }
          if (stateData.failed) setFailed(true);
        }
        setGameStateLoaded(true);
      }
    };
    loadGameState();
  }, [userId, gameDate]);

  // Save triesLeft to Firestore
  useEffect(() => {
    const saveTriesLeft = async () => {
      if (userId && gameStateLoaded) {
        const stateRef = doc(db, "userGameStates", `${userId}-${gameDate}`);
        await setDoc(stateRef, { triesLeft }, { merge: true });
      }
    };
    saveTriesLeft();
  }, [triesLeft, userId, gameDate, gameStateLoaded]);

  // Save correctGroups to Firestore
  useEffect(() => {
    const saveCorrectGroups = async () => {
      if (userId && gameStateLoaded) {
        const stateRef = doc(db, "userGameStates", `${userId}-${gameDate}`);
        await setDoc(stateRef, { correctGroups }, { merge: true });
      }
    };
    saveCorrectGroups();
  }, [correctGroups, userId, gameDate, gameStateLoaded]);

  // Save game completion/failure to Firestore
  useEffect(() => {
    const saveGameState = async () => {
      if (gameCompleted && userId) {
        const stateRef = doc(db, "userGameStates", `${userId}-${gameDate}`);
        await setDoc(
          stateRef,
          { completed: true, failed: failed },
          { merge: true }
        );
      }
    };
    saveGameState();
  }, [gameCompleted, failed, userId, gameDate]);

  // Automatically mark game as complete if no tries remain.
  useEffect(() => {
    if (triesLeft === 0) {
      setGameCompleted(true);
      setFailed(true);
      gtag_report_conversion();
    }
  }, [triesLeft]);

  // Compute solution groups if game is complete and show solution.
  useEffect(() => {
    if (gameCompleted && showSolution && allWords.length > 0) {
      const groupsObj = {};
      allWords.forEach((w) => {
        if (!groupsObj[w.category]) groupsObj[w.category] = [];
        groupsObj[w.category].push(w);
      });
      setCorrectGroups(groupsObj);
    }
  }, [gameCompleted, showSolution, allWords]);

  // Remove solved words from the current words list.
  useEffect(() => {
    if (Object.keys(correctGroups).length > 0 && words.length > 0) {
      const solvedWords = Object.values(correctGroups)
        .flat()
        .map((g) => g.word);
      const newWords = words.filter((w) => !solvedWords.includes(w.word));
      if (newWords.length !== words.length) setWords(newWords);
    }
  }, [correctGroups, words]);

  // Handling clicks on words
  const handleWordClick = (word) => {
    const solvedWords = Object.values(correctGroups)
      .flat()
      .map((g) => g.word);
    if (selectedWords.includes(word) || solvedWords.includes(word)) {
      setSelectedWords(selectedWords.filter((w) => w !== word));
      return;
    }
    const newSelection = [...selectedWords, word];
    if (newSelection.length > 4) return;
    setSelectedWords(newSelection);
  };

  // Checking the selection and grouping words correctly.
  const checkSelection = () => {
    if (selectedWords.length !== 4 || triesLeft === 0) return;

    const categoriesObj = words.reduce((acc, w) => {
      acc[w.word] = w.category;
      return acc;
    }, {});

    const freq = {};
    selectedWords.forEach((word) => {
      const cat = categoriesObj[word];
      freq[cat] = (freq[cat] || 0) + 1;
    });

    const uniqueCategories = new Set(
      selectedWords.map((word) => categoriesObj[word])
    );

    if (uniqueCategories.size === 1) {
      const newGroup = selectedWords.map((word) => ({
        word,
        category: categoriesObj[word],
      }));
      const newCorrectGroups = {
        ...correctGroups,
        [Object.keys(correctGroups).length]: newGroup,
      };
      setCorrectGroups(newCorrectGroups);
      setWords(words.filter((w) => !selectedWords.includes(w.word)));
      setSelectedWords([]);
      if (Object.keys(newCorrectGroups).length === 4) {
        setGameCompleted(true);
        gtag_report_conversion();
      }
    } else if (Object.values(freq).includes(3)) {
      setShowThreePopup(true);
      setTimeout(() => setShowThreePopup(false), 3000);
      setTriesLeft(triesLeft - 1);
      setError(true);
      setTimeout(() => {
        setError(false);
        setSelectedWords([]);
      }, 1000);
    } else {
      setTriesLeft(triesLeft - 1);
      setError(true);
      setTimeout(() => {
        setError(false);
        setSelectedWords([]);
      }, 1000);
    }
  };

  // Fetch previous games (excluding today's)
  useEffect(() => {
    const fetchPreviousGames = async () => {
      const wordsCollection = collection(db, "connections_words");
      const wordsSnapshot = await getDocs(wordsCollection);
      if (!wordsSnapshot.empty) {
        const games = wordsSnapshot.docs
          .map((doc) => ({
            date: doc.id,
            index: doc.data().index || "?",
          }))
          .filter((game) => game.date < today);
        games.sort((a, b) => new Date(b.date) - new Date(a.date));
        setPreviousGames(games);
        setCurrentPage(1);
      }
    };
    fetchPreviousGames();
  }, [today]);

  // Sharing the score
  const shareScore = () => {
    let text = "";
    if (failed) {
      text = `ניסיתי את חידה מס' ${riddleIndex} ב"קישורים"\nנסו גם: https://kishurim.fun`;
    } else {
      text = `🎉 פתרתי את "קישורים" מס' ${riddleIndex} עם ${
        5 - triesLeft
      } טעויות! נסו גם: https://kishurim.fun`;
    }

    if (navigator.share) {
      navigator
        .share({ text })
        .then(() => console.log("Successful share"))
        .catch((error) => console.error("Error sharing", error));
    } else {
      navigator.clipboard.writeText(text);
      alert("התוצאה הועתקה! שתפו עם חברים!");
    }
  };

  return (
    <div className="app-container">
      <div className="top-bar">
        <button
          className="top-bar-button"
          onClick={() => setShowInstructions(true)}
        >
          איך לשחק ❓
        </button>
        <button
          className="top-bar-button"
          onClick={() => setShowPreviousGames(true)}
        >
          משחקים קודמים 📅
        </button>
      </div>

      <img src={logo} alt="logo" className="logo" />
      <h2 style={{ marginTop: "-15px" }}>חידה יומית מס' {riddleIndex}</h2>

      <div className="game-wrapper">
        {!gameCompleted && !failed && dailyRiddleExists && (
          <p className="tries-left">נותרו {triesLeft} טעויות</p>
        )}
        {!dailyRiddleExists ? (
          <div className="no-daily-riddle">
            <p>
              המשחק היומי עדיין לא פורסם, אך ניתן לשחק במשחקים מהימים הקודמים.
            </p>
          </div>
        ) : (
          !showSolution &&
          !failed && (
            <WordGrid
              words={words}
              selectedWords={selectedWords}
              handleWordClick={handleWordClick}
              error={error}
            />
          )
        )}
        {gameCompleted && failed ? (
          <div
            className="failure-message"
            style={{
              cursor: "default",
              padding: "1rem",
              background: "#ffeeba",
              textAlign: "center",
              margin: "0 0",
            }}
          >
            <h2>נגמרו לך הטעויות להיום :(</h2>
            <p>הורידו את האפליקציה ותוכלו להמשיך ולשחק עד שתפתרו!</p>
            <a
              href="https://play.google.com/store/apps/details?id=com.kupigames.kishurim"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={googlePlayBadge}
                alt="Download on Google Play"
                style={{ maxWidth: "200px" }}
              />
            </a>
          </div>
        ) : (
          Object.keys(correctGroups).length > 0 && (
            <CorrectGroups correctGroups={correctGroups} />
          )
        )}
        {!gameCompleted && triesLeft > 0 && dailyRiddleExists ? (
          <button
            className="check-button"
            onClick={checkSelection}
            disabled={triesLeft === 0}
          >
            {" "}
            בדוק
          </button>
        ) : gameCompleted ? null : (
          dailyRiddleExists && (
            <button
              className="reveal-button"
              onClick={() => {
                setShowSolution(true);
                setGameCompleted(true);
              }}
            >
              חשוף תשובה
            </button>
          )
        )}
        {gameCompleted && (
          <button className="share-button" onClick={shareScore}>
            📤 שתף תוצאה
          </button>
        )}
        <div className="donation-banner">
          <a href="https://buymeacoffee.com/kuperman5">
            פרגנו לי בכוס קפה, או בירה, מה שתרצו :) 🙏
          </a>
        </div>
        <h4 style={{ marginTop: "40px" }}>ניתן למצוא גם בחנות האפליקציות</h4>
        <div className="google-play-link" style={{ textAlign: "center" }}>
          <a
            href="https://play.google.com/store/apps/details?id=com.kupigames.kishurim"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={googlePlayBadge}
              alt="Download on Google Play"
              style={{ maxWidth: "200px" }}
            />
          </a>
        </div>
      </div>

      <AnimatePresence>
        {showThreePopup && (
          <motion.div
            key="three-popup"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
            style={{
              position: "fixed",
              top: "10px",
              left: 0,
              right: 0,
              margin: "50px 50px",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              color: "white",
              padding: "20px 40px",
              borderRadius: "8px",
              zIndex: 1000,
              fontSize: "2rem",
              textAlign: "center",
            }}
          >
            הצלחת 3 מתוך 4 מילים
          </motion.div>
        )}
      </AnimatePresence>

      {showInstructions && (
        <div
          className="modal-overlay"
          onClick={() => setShowInstructions(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>📜 איך לשחק</h2>
            <p>לפניכם 16 מילים</p>
            <p>עליכם לחפש 4 קבוצות של 4 מילים</p>
            <p>כאשר בכל קבוצה המילים קשורות בצורה מסויימת</p>
            <p>בהצלחה!</p>
            <button
              className="check-button"
              onClick={() => setShowInstructions(false)}
            >
              סגור
            </button>
          </div>
        </div>
      )}

      {showPreviousGames && (
        <div
          className="modal-overlay"
          onClick={() => setShowPreviousGames(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ textAlign: "center" }}
          >
            <h2>משחקים קודמים</h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "10px",
                marginTop: "20px",
                direction: "ltr",
              }}
            >
              {previousGames.map((game) => (
                <button
                  key={game.date}
                  onClick={() =>
                    window.open(`/?id=${encryptDate(game.date)}`, "_blank")
                  }
                  style={{
                    padding: "10px",
                    background: "#4285F4",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "1rem",
                    cursor: "pointer",
                  }}
                >
                  {game.index}
                </button>
              ))}
            </div>
            <button
              className="check-button"
              onClick={() => {
                setShowPreviousGames(false);
                setCurrentPage(1);
              }}
              style={{ marginTop: "40px" }}
            >
              סגור
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionsGame;
